// The rules for turning raw input into a list of people to call.
//
// Kept apart from the UI deliberately: everything here decides whether a real
// phone rings, and none of it needs a browser, a service or a component to be
// checked. See RecipientSources.test.ts.
// Type-only: `import type` is erased at compile time, so this module pulls in
// no service and no axios, and stays testable on its own.
import type { RecipientInput } from 'api/services/voiceCampaigns.service';

/**
 * The comparison key for "same person".
 *
 * Last ten digits, matching what the backend does when it checks number
 * ownership — +91 22 6519 7350, 02265197350 and 912265197350 are one number, and
 * treating them as three is how a list of 500 becomes 520 calls.
 */
export const dedupeKey = (phone: string): string => {
  const d = (phone || '').replace(/[^0-9]/g, '');
  return d.length > 10 ? d.slice(-10) : d;
};

/** Anything that could not be a phone number is dropped rather than sent. */
export const looksDiallable = (phone: string): boolean => {
  const d = (phone || '').replace(/[^0-9]/g, '');
  return d.length >= 5 && d.length <= 18;
};

export interface RecipientSelection {
  recipients: RecipientInput[];
  /** Dropped as a repeat of an earlier entry in the same submission. */
  duplicates: number;
  /** Dropped as not a phone number at all. */
  invalid: number;
}

export const EMPTY_SELECTION: RecipientSelection = { recipients: [], duplicates: 0, invalid: 0 };

/**
 * Collapses raw rows into what will actually be queued.
 *
 * First occurrence wins, so the params from the earliest row survive — with a
 * CSV that is the top row, which is the one a person looking at the file expects.
 *
 * @param allowRepeats keep repeated numbers instead of dropping them. Invalid
 *   rows are dropped either way: "call this person twice" is a decision an
 *   operator can sensibly make, "call this non-number" is not. Repeats are
 *   still COUNTED when kept, so the screen can say how many there are — a
 *   silent doubling of a list is exactly what the dedupe existed to prevent.
 */
export const collapse = (rows: RecipientInput[], allowRepeats = false): RecipientSelection => {
  const seen = new Set<string>();
  const out: RecipientInput[] = [];
  let duplicates = 0, invalid = 0;
  for (const r of rows) {
    const phone = (r.phone || '').trim();
    if (!looksDiallable(phone)) { invalid++; continue; }
    const key = dedupeKey(phone);
    if (seen.has(key)) {
      duplicates++;
      if (!allowRepeats) continue;
    }
    seen.add(key);
    out.push(r);
  }
  return { recipients: out, duplicates, invalid };
};

/**
 * Headings people reach for when they want to set the presenting number — and
 * which do NOT do that.
 *
 * A caller ID is not a per-recipient field anywhere in the chain:
 * `CampaignQueueService` reads ONE `caller_id` from the request body and writes
 * it to `callFrom` on every row in the batch. A column of them cannot override
 * anything, because there is nothing per-row to override.
 *
 * What a `caller_id` column actually does is become a VARIABLE — it travels to
 * the agent in `custom_data` alongside the name — so the file is accepted, the
 * rows queue, and the number presented is still the campaign's. Silently. This
 * list exists so the screen can say so instead.
 */
export const CALLER_ID_HEADERS = [
  'caller_id', 'callerid', 'caller', 'from', 'from_number', 'fromnumber', 'did', 'cli',
];

/** The caller-id-looking headings in a file, if any. Used only to warn. */
export const callerIdHeadings = (header: string[]): string[] =>
  header
    .map(h => (h || '').trim().toLowerCase())
    .filter(h => CALLER_ID_HEADERS.includes(h));

/**
 * Does this number carry a country code the switch can dial?
 *
 * Nothing in the chain adds one. A bare ten-digit number passes the browser's
 * check, passes the backend's `^\+?[0-9]{5,18}$`, is stored verbatim, and then
 * Asterisk answers `Allocation failed` — it has been asked to dial a national
 * number with no country code. Recorded against a real incident, 2026-08-14.
 *
 * The normaliser elsewhere that strips to the last ten digits exists ONLY to
 * compare two numbers for equality; it never touches what gets dialled.
 *
 * Deliberately a warning rather than a block: this cannot know every dial plan,
 * and refusing a number somebody knows is right would be worse than saying so.
 */
export const looksInternational = (n: string): boolean => {
  const t = (n || '').trim();
  if (!t) return false;
  if (t.startsWith('+')) return true;
  const d = t.replace(/[^0-9]/g, '');
  if (d.startsWith('00')) return true;
  // A SINGLE leading zero is the national trunk prefix, not a country code, and
  // it makes the number one digit longer — so a plain length test reads
  // 09228034171 as international. It is the same undialable national number as
  // 9228034171 with a 0 in front, and the switch refuses both.
  if (d.startsWith('0')) return false;
  return d.length > 10;
};

/** Header names that mean "this is the number to call". */
export const PHONE_HEADERS = ['phone', 'mobile', 'number', 'msisdn', 'contact', 'phone_number', 'callto'];

/**
 * A minimal CSV reader — quoted fields, embedded commas and newlines.
 *
 * Deliberately not a library: the files here are exports from spreadsheets, and
 * the failure mode that matters is a quoted "Doe, John" splitting into two
 * columns and shifting every number one place to the left.
 */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [], field = '', quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === ',') { row.push(field); field = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
    field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(c => c.trim() !== ''));
};

/**
 * Turns parsed CSV rows into recipients.
 *
 * A file with no recognisable phone header probably has no header at all, so
 * every row is treated as data rather than silently eating the first record.
 */
export const recipientsFromCsv = (rows: string[][]): { recipients: RecipientInput[]; hasHeader: boolean; column: string } => {
  if (!rows.length) return { recipients: [], hasHeader: false, column: '' };

  const header = rows[0].map(h => h.trim().toLowerCase());
  const phoneIdx = header.findIndex(h => PHONE_HEADERS.includes(h) || PHONE_HEADERS.some(k => h.includes(k)));
  const hasHeader = phoneIdx >= 0;
  const col = hasHeader ? phoneIdx : 0;
  const body = hasHeader ? rows.slice(1) : rows;

  const recipients: RecipientInput[] = [];
  for (const r of body) {
    const phone = (r[col] || '').trim();
    if (!phone) continue;
    const params: Record<string, any> = {};
    if (hasHeader) {
      r.forEach((v, i) => {
        const key = header[i];
        if (i === col || !key || !v.trim()) return;
        params[key] = v.trim();
      });
    }
    recipients.push({ phone, params: Object.keys(params).length ? params : undefined });
  }
  return { recipients, hasHeader, column: hasHeader ? rows[0][col].trim() : '' };
};
